import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  Typography,
  TextField,
  Box,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Add,
  Folder,
  FolderOpen,
  AccessTime,
  Image as ImageIcon,
  CloudUpload,
  Delete,
  Person
} from '@mui/icons-material';

import sessionService from '../api/sessionService';

const formatTimeAgo = (dateString) => {
  if (!dateString) return 'Unknown';
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  } catch (error) {
    return 'Unknown';
  }
};

const SessionSelectionDialog = ({ open, onClose, onSessionSelected, onSessionDeleted, title = "Select Session", refreshTrigger }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newSessionName, setNewSessionName] = useState('');
  const [newSessionDescription, setNewSessionDescription] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, session: null });
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    if (open) {
      loadSessions();
    }
  }, [open, statusFilter, refreshTrigger]);

  const loadSessions = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await sessionService.getSessions({
        status: statusFilter,
        sortBy: 'updatedAt',
        sortOrder: 'desc',
        limit: 50
      });
      setSessions(response.sessions || []);
    } catch (err) {
      setError('Failed to load sessions: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSession = async () => {
    if (!newSessionName.trim()) {
      setError('Session name is required');
      return;
    }

    setCreateLoading(true);
    setError('');
    try {
      const sessionData = {
        name: newSessionName.trim(),
        description: newSessionDescription.trim(),
        settings: {
          autoAnalysis: false
        }
      };

      const newSession = await sessionService.createSession(sessionData);
      setSessions(prev => [newSession, ...prev]);
      setNewSessionName('');
      setNewSessionDescription('');
      setShowCreateForm(false);
      setSelectedSession(newSession);
    } catch (err) {
      setError('Failed to create session: ' + err.message);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDeleteSession = (session, event) => {
    event.stopPropagation();
    setDeleteDialog({ open: true, session });
  };

  const confirmDeleteSession = async () => {
    if (!deleteDialog.session) return;

    setDeleteLoading(true);
    setError('');
    
    try {
      await sessionService.deleteSession(deleteDialog.session.uniqueId);
      setSessions(prev => prev.filter(s => s.uniqueId !== deleteDialog.session.uniqueId));
      
      if (selectedSession?.uniqueId === deleteDialog.session.uniqueId) {
        setSelectedSession(null);
      }
      
      // Notify parent component about the deletion
      if (onSessionDeleted) {
        onSessionDeleted(deleteDialog.session);
      }
      
      setDeleteDialog({ open: false, session: null });
      console.log(`Session "${deleteDialog.session.name}" deleted successfully`);
      
    } catch (err) {
      setError('Failed to delete session: ' + err.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  const cancelDeleteSession = () => {
    setDeleteDialog({ open: false, session: null });
  };

  const handleSessionSelect = (session) => {
    setSelectedSession(session);
  };

  const handleSessionDoubleClick = (session) => {
    setSelectedSession(session);
    if (onSessionSelected) {
      onSessionSelected(session);
    }
    handleClose();
  };

  const handleConfirm = () => {
    if (selectedSession && onSessionSelected) {
      onSessionSelected(selectedSession);
    }
    handleClose();
  };

  const handleClose = () => {
    setSelectedSession(null);
    setShowCreateForm(false);
    setNewSessionName('');
    setNewSessionDescription('');
    setError('');
    setDeleteDialog({ open: false, session: null });
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        aria-labelledby="session-dialog-title"
      >
        <DialogTitle id="session-dialog-title">
          <Box display="flex" alignItems="center" gap={1}>
            <Folder />
            {title}
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {/* Status Filter */}
          <Box mb={2}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="archived">Archived</MenuItem>
                <MenuItem value="all">All</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Error Display */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Create New Session Form */}
          {showCreateForm && (
            <Box mb={3} p={2} border={1} borderColor="grey.300" borderRadius={1}>
              <Typography variant="h6" gutterBottom>
                Create New Session
              </Typography>
              <TextField
                fullWidth
                label="Session Name"
                value={newSessionName}
                onChange={(e) => setNewSessionName(e.target.value)}
                margin="normal"
                required
                inputProps={{ maxLength: 200 }}
              />
              <TextField
                fullWidth
                label="Description (Optional)"
                value={newSessionDescription}
                onChange={(e) => setNewSessionDescription(e.target.value)}
                margin="normal"
                multiline
                rows={3}
                inputProps={{ maxLength: 1000 }}
              />
              <Box mt={2} display="flex" gap={1}>
                <Button
                  variant="contained"
                  onClick={handleCreateSession}
                  disabled={createLoading || !newSessionName.trim()}
                  startIcon={createLoading ? <CircularProgress size={20} /> : <Add />}
                >
                  Create Session
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setShowCreateForm(false)}
                  disabled={createLoading}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          )}

          {/* Session List */}
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Your Sessions
              </Typography>
              {!showCreateForm && (
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={() => setShowCreateForm(true)}
                  size="small"
                >
                  New Session
                </Button>
              )}
            </Box>

            {loading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : sessions.length === 0 ? (
              <Box textAlign="center" p={3}>
                <Typography color="text.secondary">
                  No sessions found. Create a new session to get started.
                </Typography>
              </Box>
            ) : (
              <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                {sessions.map((session) => (
                  <ListItem key={session.uniqueId} disablePadding>
                    <ListItemButton
                      selected={selectedSession?.uniqueId === session.uniqueId}
                      onClick={() => handleSessionSelect(session)}
                      onDoubleClick={() => handleSessionDoubleClick(session)}
                      sx={{
                        border: selectedSession?.uniqueId === session.uniqueId ? 2 : 1,
                        borderColor: selectedSession?.uniqueId === session.uniqueId ? 'primary.main' : 'grey.300',
                        borderRadius: 1,
                        mb: 1,
                        p: 2
                      }}
                    >
                      <Box display="flex" alignItems="center" width="100%">
                        {selectedSession?.uniqueId === session.uniqueId ? (
                          <FolderOpen color="primary" sx={{ mr: 2 }} />
                        ) : (
                          <Folder color="action" sx={{ mr: 2 }} />
                        )}
                        
                        <Box flexGrow={1}>
                          <Typography variant="subtitle1" fontWeight="medium">
                            {session.name}
                          </Typography>
                          
                          {session.description && (
                            <Typography variant="body2" color="text.secondary" noWrap>
                              {session.description}
                            </Typography>
                          )}
                          
                          <Box display="flex" alignItems="center" gap={2} mt={1}>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <ImageIcon fontSize="small" color="action" />
                              <Typography variant="caption">
                                {session.formattedStats?.totalImages || 0} images
                              </Typography>
                            </Box>
                            
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <AccessTime fontSize="small" color="action" />
                              <Typography variant="caption">
                                {formatTimeAgo(session.stats?.lastActivity || session.updatedAt)}
                              </Typography>
                            </Box>
                            
                            {session.createdBy && (
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <Person fontSize="small" color="action" />
                                <Typography variant="caption">
                                  {session.createdBy.username || session.createdBy.email || 'Unknown user'}
                                </Typography>
                              </Box>
                            )}
                            
                            <Chip
                              label={session.status}
                              size="small"
                              color={session.status === 'active' ? 'success' : 'default'}
                              variant="outlined"
                            />
                          </Box>
                        </Box>
                        
                        {/* Delete Button */}
                        <Box ml={1}>
                          <Tooltip title="Delete Session">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(event) => handleDeleteSession(session, event)}
                              sx={{ 
                                '&:hover': { 
                                  backgroundColor: 'error.light',
                                  color: 'white'
                                }
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={!selectedSession}
            startIcon={<CloudUpload />}
          >
            Select Session
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={cancelDeleteSession}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1} color="error.main">
            <Delete />
            Confirm Session Deletion
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body1" gutterBottom>
              <strong>This action cannot be undone!</strong>
            </Typography>
            <Typography variant="body2">
              Deleting this session will permanently remove:
            </Typography>
            <Box component="ul" sx={{ mt: 1, mb: 0 }}>
              <li>The session "{deleteDialog.session?.name}"</li>
              <li>All images in this session ({deleteDialog.session?.formattedStats?.totalImages || 0} images)</li>
              <li>All image variants (thumbnails, analysis images)</li>
              <li>All associated metadata</li>
            </Box>
          </Alert>
          
          <Typography variant="body1">
            Are you sure you want to delete the session "{deleteDialog.session?.name}"?
          </Typography>
        </DialogContent>
        
        <DialogActions>
          <Button 
            onClick={cancelDeleteSession}
            disabled={deleteLoading}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmDeleteSession}
            color="error"
            variant="contained"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={20} /> : <Delete />}
          >
            {deleteLoading ? 'Deleting...' : 'Delete Session'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SessionSelectionDialog;
